import { useEffect, useRef, useState } from 'react';
import { trpc } from '@web/lib/trpc';

import { useIntersection } from '@mantine/hooks';

import { ScrollArea } from '@web/components/ui/scroll-area';

import { MessageBubble } from './message-bubble';
import { SuggestionsBubble } from './suggestions-bubble';
import { MessageInput } from './message-input';
import { useAuthSession } from '@web/auth/hooks';

function formatMessageDate(date: Date) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString();
}

export function MessagesArea({ contactId }: { contactId: string }) {
  const {
    messaging: { getMessages: getMessagesUtils },
  } = trpc.useUtils();

  const { user } = useAuthSession();
  const userId = user?.id ?? '';

  const { data: messagesData, fetchNextPage: fetchMoreMessages } =
    trpc.messaging.getMessages.useInfiniteQuery(
      { userId, contactId },

      {
        staleTime: Infinity,

        getNextPageParam: (lastPage) => {
          const lastMessage = lastPage.at(-1);
          if (!lastMessage) return null; // last page empty

          return {
            dateTo: lastMessage.timestamp,
          };
        },

        enabled: !!user,
      },
    );

  useEffect(() => {
    getMessagesUtils.setInfiniteData(
      { contactId, userId },

      (prevData) => ({
        pageParams:
          prevData?.pageParams.map((params, idx) => {
            const currPage = prevData.pages.at(idx);
            const prevPage = idx > 0 ? prevData.pages.at(idx - 1) : undefined;

            const dateFrom = currPage?.at(-1)?.timestamp;
            const dateTo = prevPage?.at(-1)?.timestamp;

            return { ...params, dateFrom, dateTo };
          }) ?? [],
        pages: prevData?.pages ?? [],
      }),
    );
  }, [messagesData, contactId, getMessagesUtils, userId]);

  const { mutateAsync: getMessageSuggestions } =
    trpc.messaging.getMessageSuggestions.useMutation();

  const messages = messagesData?.pages.flat().toReversed() ?? [];

  const messagesLoaded = messages.length > 0;

  const lastMessage = messages.at(-1);
  const lastMessageSuggestions =
    lastMessage?.direction === 'inbound'
      ? lastMessage.replySuggestions
      : undefined;

  const firstMessageId = messages.at(0)?.id;
  const prevFirstMessageId = useRef<string | null>(null);
  const prevLastMessageId = useRef<string | null>(null);

  const prevFirstMessageRef = useRef<HTMLDivElement>(null);
  const firstMessageRef = useRef<HTMLDivElement>(null);
  const middleMessageRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const replySuggestionsRef = useRef<HTMLDivElement | null>(null);

  const { ref: firstMessageObserverRef, entry: firstMessageObserver } =
    useIntersection({
      root: firstMessageRef.current,
      threshold: 1,
    });

  const { ref: middleMessageObserverRef, entry: middleMessageObserver } =
    useIntersection({
      root: middleMessageRef.current,
      threshold: 1,
    });

  useEffect(() => {
    if (firstMessageObserver?.isIntersecting) void fetchMoreMessages();
  }, [firstMessageObserver, fetchMoreMessages]);

  useEffect(() => {
    if (lastMessage && lastMessage.id !== prevLastMessageId.current) {
      prevLastMessageId.current = lastMessage.id;

      if (middleMessageObserver?.isIntersecting) {
        lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
      }

      if (
        lastMessage.direction === 'inbound' &&
        lastMessage.replySuggestions.length === 0
      )
        void getMessageSuggestions({
          contactId,
          messageId: lastMessage.id,
        });
    }
  }, [lastMessage, middleMessageObserver, contactId, getMessageSuggestions]);

  useEffect(() => {
    if (firstMessageId) {
      prevFirstMessageId.current = firstMessageId;

      prevFirstMessageRef.current?.scrollIntoView({ behavior: 'instant' });
    }
  }, [firstMessageId]);

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'instant' });
  }, [messagesLoaded]);

  useEffect(() => {
    if (lastMessage?.direction === 'outbound')
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lastMessage]);

  useEffect(() => {
    if (lastMessageSuggestions)
      replySuggestionsRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
  }, [lastMessageSuggestions]);

  const [initText, setInitText] = useState('');

  const messagesByDate = messages.reduce<Record<string, typeof messages>>(
    (groups, message) => {
      const date = new Date(message.timestamp);
      const dateString = date.toDateString();

      return {
        ...groups,
        [dateString]: [...(groups[dateString] ?? []), message],
      };
    },
    {},
  );

  return (
    <>
      <ScrollArea className="flex-1 px-5 py-1 scrollbar-thin">
        <div className="flex flex-col gap-3 py-6">
          {Object.entries(messagesByDate).map(([dateString, dateMessages]) => (
            <div key={dateString} className="flex flex-col gap-3">
              <div className="my-3 flex justify-center">
                <div className="bg-gray-100 text-gray-500 rounded-full px-3 py-0.5 text-[10px] font-medium shadow-sm">
                  {formatMessageDate(new Date(dateString))}
                </div>
              </div>
              {dateMessages.map((message, idx) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  ref={
                    idx === 0 &&
                    dateMessages ===
                      messagesByDate[Object.keys(messagesByDate)[0]]
                      ? firstMessageObserverRef
                      : idx === messages.length - 2
                        ? middleMessageObserverRef
                        : idx === messages.length - 1
                          ? lastMessageRef
                          : message.id === prevFirstMessageId.current
                            ? prevFirstMessageRef
                            : null
                  }
                  onSelectReply={(message) => {
                    setInitText(message.content);
                  }}
                />
              ))}
            </div>
          ))}

          {lastMessageSuggestions && lastMessageSuggestions.length > 0 && (
            <SuggestionsBubble
              suggestions={lastMessageSuggestions}
              ref={replySuggestionsRef}
              onSelectReply={(message) => {
                setInitText(message.content);
              }}
            />
          )}
        </div>
      </ScrollArea>

      <MessageInput initText={initText} activeContactId={contactId} />
    </>
  );
}
